import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useShop } from "@/context/ShopContext";

export default function OrderConfirmation() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id") || "";
  const { clearCart } = useShop();
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("checking"); // checking | paid | pending | error
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid" && data.order_id) {
          const { data: orderData } = await api.get(`/checkout/status/${sessionId}/order`);
          setOrder(orderData);
          setStatus("paid");
          if (!cleared.current) { cleared.current = true; clearCart(); }
          return;
        }
        attempts += 1;
        if (attempts < 15) setTimeout(poll, 2000);
        else setStatus("pending");
      } catch {
        attempts += 1;
        if (attempts < 15) setTimeout(poll, 2000);
        else setStatus("error");
      }
    };
    poll();
  }, [sessionId, clearCart]);

  if (status === "checking") {
    return (
      <div className="pt-40 pb-40 text-center" data-testid="order-confirming">
        <Loader2 size={40} className="text-[#D4AF37] animate-spin mx-auto" />
        <h1 className="font-serif-display text-3xl text-white mt-8">Confirming your payment...</h1>
        <p className="font-sans-body text-sm text-[#B8B8B8] font-light mt-3">This takes just a moment.</p>
      </div>
    );
  }

  if (status === "pending") {
    return (
      <div className="pt-40 pb-40 text-center px-6" data-testid="order-pending">
        <h1 className="font-serif-display text-3xl text-white">Payment is being processed</h1>
        <p className="font-sans-body text-sm text-[#B8B8B8] font-light mt-4 max-w-md mx-auto">
          We're still confirming your payment with Stripe. You will receive an email confirmation
          as soon as the order is finalized.
        </p>
        <Link to="/shop" className="btn-gold mt-10 inline-flex">Back to Shop</Link>
      </div>
    );
  }

  if (status === "error" || !order) {
    return (
      <div className="pt-40 pb-40 text-center px-6" data-testid="order-error">
        <h1 className="font-serif-display text-3xl text-white">We couldn't confirm this order</h1>
        <p className="font-sans-body text-sm text-[#B8B8B8] font-light mt-4 max-w-md mx-auto">
          If your payment was completed, your order will still be processed and confirmed by email.
          Please contact us if you have any questions.
        </p>
        <Link to="/contact" className="btn-outline-gold mt-10 inline-flex">Contact Us</Link>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 max-w-3xl mx-auto px-4 sm:px-8" data-testid="order-confirmation-page">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
        <div className="text-center">
          <CheckCircle2 size={56} strokeWidth={1} className="text-[#D4AF37] mx-auto" />
          <p className="eyebrow mt-6">Order Confirmed</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-white font-light mt-4">
            Thank you, {order.customer.name.split(" ")[0]}.
          </h1>
          <p className="font-sans-body text-sm text-[#B8B8B8] font-light mt-4">
            A confirmation email is on its way to <span className="text-white">{order.customer.email}</span>.
          </p>
        </div>

        <div className="gold-hairline bg-[#0D0D0D] p-8 mt-12 space-y-6" data-testid="order-details">
          <div className="flex items-center justify-between">
            <span className="font-sans-body text-[10px] uppercase tracking-[0.3em] text-[#C6A15B]">Order Number</span>
            <span className="font-mono-price text-lg text-[#D4AF37]" data-testid="order-number">{order.order_number}</span>
          </div>
          <div className="gold-hairline-t pt-6 space-y-4">
            {order.items.map((i) => (
              <div key={`${i.product_id}-${i.size}`} className="flex justify-between font-sans-body text-sm">
                <span className="text-white">{i.name} <span className="text-[#B8B8B8]">· {i.size} × {i.quantity}</span></span>
                <span className="font-mono-price text-white">${(i.price * i.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="gold-hairline-t pt-6 space-y-2 font-sans-body text-sm text-[#B8B8B8]">
            <div className="flex justify-between"><span>Subtotal</span><span className="font-mono-price">${order.subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Shipping ({order.shipping_method})</span><span className="font-mono-price">${order.shipping_fee.toFixed(2)}</span></div>
            {order.tax_total > 0 && <div className="flex justify-between"><span>Tax</span><span className="font-mono-price">${order.tax_total.toFixed(2)}</span></div>}
            <div className="flex justify-between pt-3 gold-hairline-t">
              <span className="uppercase tracking-[0.2em] text-white text-xs">Total Paid</span>
              <span className="font-mono-price text-lg text-[#D4AF37]" data-testid="order-total">${order.total.toFixed(2)}</span>
            </div>
          </div>
          <div className="gold-hairline-t pt-6 font-sans-body text-sm text-[#B8B8B8] font-light">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C6A15B] mb-2">Shipping To</p>
            <p>
              {order.customer.name}<br />
              {order.customer.address_line1}{order.customer.address_line2 ? `, ${order.customer.address_line2}` : ""}<br />
              {order.customer.city}{order.customer.state ? `, ${order.customer.state}` : ""} {order.customer.postal_code}, {order.customer.country}
            </p>
          </div>
        </div>

        <div className="text-center mt-12">
          <Link to="/shop" className="btn-gold" data-testid="order-continue-shopping">Continue Shopping</Link>
        </div>
      </motion.div>
    </div>
  );
}
